import React, { useEffect, useState } from 'react';
import { FlightDetails, FlightStatus } from '../interfaces/flight.details';
import aeroplane from '../assets/airplane-svgrepo-com.png';
import { BsLuggage } from "react-icons/bs";
import { formatDate } from '../utils/common.utils';
import { RemarkToCodeMapping } from '@industronics/fids-utils';
import SafeFlightLogo from './SafeFlightLogo';
import TimeBlock from './TimeBlock';

interface FlightDetailModalProps {
    flight: FlightDetails;
    onClose: () => void;
    modalTitle: string;
}

const FlightDetailPopup: React.FC<FlightDetailModalProps> = ({ flight, onClose, modalTitle }) => {

    if (!flight) {
        return <div>Loading...</div>;
    }

    const handleGateChange = (flight: FlightDetails) => {
        if (!flight.loungeCode) return '-';
        const firstChar = flight.loungeCode.match(/[A-Za-z]/)?.[0] || "";
        const numericPart = flight.loungeCode.match(/\d+/)?.[0].replace(/^0+/, "") || "";
        return firstChar + numericPart;
    };

    const status = (remark: FlightStatus): React.ReactNode => {
        switch (remark?.key) {
            case RemarkToCodeMapping.LAST_BAG:
            case RemarkToCodeMapping.FIRST_BAG:
            case RemarkToCodeMapping.OPEN:
            case RemarkToCodeMapping.BOARDING:
            case RemarkToCodeMapping.DEPARTED:
            case RemarkToCodeMapping.LANDED:
                return <span className="bg-green-100 text-green-800 text-xs me-2 px-2.5 py-0.5 rounded border border-green-400">{remark.description}</span>;
            case RemarkToCodeMapping.CANCELLED:
            case RemarkToCodeMapping.DELAYED:
            case RemarkToCodeMapping.NEW_GATE:
                return <span className="bg-red-100 text-red-800 text-xs me-2 px-2.5 py-0.5 rounded border border-red-400">{remark.description}</span>;
            case RemarkToCodeMapping.GATE_CLOSED:
                return <span className="bg-gray-100 text-gray-800 text-xs me-2 px-2.5 py-0.5 rounded border border-gray-400">{remark.description}</span>;
            case RemarkToCodeMapping.GATE_OPEN:
                return <span className="bg-blue-100 text-blue-800 text-xs me-2 px-2.5 py-0.5 rounded border border-blue-400">{remark.description}</span>
            case RemarkToCodeMapping.FINAL_CALL:
                return <span className="bg-orange-100 text-orange-800 text-xs me-2 px-2.5 py-0.5 rounded border border-orange-400">{remark.description}</span>;
            default:
                return remark?.description;
        }
    };


    return (
        <div
            id="default-modal"
            tabIndex={-1}
            className="fixed top-0 right-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50"
        >
            <div className="relative p-2 w-full max-w-xl max-h-full">
                <div className="relative p-2 bg-white rounded-lg shadow dark:bg-gray-700">
                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                            data-modal-hide="default-modal"
                            onClick={onClose}
                        >
                            <svg
                                className="w-3 h-3"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 14 14"
                            >
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                                />
                            </svg>
                            <span className="sr-only">Close modal</span>
                        </button>
                    </div>

                    <div className="flex items-center justify-center border-b border-blue-400">
                        <h3 className="text-2xl pb-2 font-semibold text-gray-600">
                            {modalTitle} Details
                        </h3>
                    </div>

                    <div className="px-4 py-2 md:p-7 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-2xl font-bold text-gray-900">
                                {flight?.flightNumber}
                            </h3>
                            <SafeFlightLogo dimension='0180x0045' mainFlightNumber={flight.flightNumber} logoSize={{ width: 120, height: 80 }} />
                            <div className='font-semibold'>
                                {status(flight?.remarkCode)}
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-gray-500">{flight?.stations[0]?.city}</p>
                            </div>
                            <div>
                                <img src={aeroplane} alt="IASB Logo" className="h-10" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-500">{flight?.stations[flight?.stations.length - 1]?.city}</p>
                            </div>
                        </div>

                        <div className="space-y-6">

                            <div className="flex justify-between text-left gap-x-4">
                                <div className="flex flex-col items-center">
                                    <p className="text-md font-semibold text-gray-600 dark:text-gray-400">Scheduled</p>
                                    <TimeBlock cssClass='text-sm font-bold text-gray-900' date={flight.scheduledTravelTime} />
                                </div>


                                <div className="flex flex-col text-left items-center">
                                    <p className="text-md font-semibold text-gray-600 dark:text-gray-400">EST</p>
                                    {flight.ETA && flight.ETA !== flight.scheduledTravelTime && <TimeBlock cssClass='text-sm font-bold text-gray-900' date={flight.ETA} />}
                                </div>
                                <div className="flex flex-col text-left items-center">
                                    <p className="text-md font-semibold text-gray-600 dark:text-gray-400">Actual Time</p>
                                    <TimeBlock cssClass='text-sm font-bold text-gray-900' date={flight.actualArrivalDepartureTime} />
                                </div>


                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className='flex justify-between space-x-2'>
                                    <div className="text-left">
                                        <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Terminal</p>
                                        <p className="text-sm font-bold text-gray-900">{flight?.terminal?.code}</p>
                                    </div>
                                    <div className="text-left">
                                        <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Gate</p>
                                        <p className="text-sm font-bold text-gray-900">{handleGateChange(flight)}</p>
                                    </div>
                                </div>
                                <div className='col-span-2'>
                                    {modalTitle === 'Arrival' ?
                                        (
                                            <div className="text-left">
                                                <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Reclaim</p>
                                                <p className="text-sm font-bold text-gray-900 inline-flex items-center">
                                                    {flight?.loungReclaimCode?.replace(/0+/g, '')} {flight?.loungReclaimCode && <BsLuggage className="ml-2 text-green-700" size={20} />}
                                                </p>
                                            </div>
                                        ) :
                                        (
                                            <div className="text-left">
                                                <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 flex items-s">Check-In</p>
                                                {
                                                    flight?.checkInStatus?.key !== RemarkToCodeMapping.DESK_CLOSED ? (
                                                        <div className="text-sm font-bold text-gray-900 flex inline-flex">
                                                            <div className='pt-1'><BsLuggage className="text-green-700" size={20} /></div>
                                                            <div className='ml-2 pt-1'>
                                                                <div>{flight?.checkInDeskRange}</div>
                                                                {(flight?.checkInRowOpeningTime || flight?.checkInRowClosingTime) && (<div><span className='text-sm font-semibold text-gray-600 '>
                                                                    <span className='text-blue-800 font-semibold'>Opening Time:</span></span> {(formatDate(flight?.checkInRowOpeningTime, false))} <span className='text-blue-800 font-semibold'>Closing Time:</span> <span>{formatDate(flight?.checkInRowClosingTime, false)}</span>
                                                                </div>)}
                                                            </div>
                                                        </div>
                                                    ) : (<p className='text-sm font-bold text-gray-900'>{flight?.checkInStatus?.description}</p>)}
                                            </div>
                                        )}
                                </div>
                            </div>
                        </div>

                        {Array.isArray(flight?.codeShareFlight) && flight?.codeShareFlight.length > 0 && (
                            <div>
                                <p className="text-sm font-semibold text-left text-gray-600 dark:text-gray-400">Code Share</p>
                                <div className="grid grid-cols-3 gap-2 mt-2">
                                    {flight?.codeShareFlight.map((item, index) => (
                                        <span
                                            key={index}
                                            className="bg-blue-50 text-gray-800 text-xs font-semibold me-2 px-2.5 py-0.5 rounded dark:bg-gray-700 dark:text-blue-400 border border-blue-400"
                                        >
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlightDetailPopup;
